/**
 * useChatWindowEvents — the four window-event listener effects lifted out of
 * ChatBox (delegation pattern). Each useEffect body + its byte-identical
 * dependency array is reproduced VERBATIM from ChatBox: xavior:send-chat-message,
 * xavior:log-chat-message, xavior:module-archived, and xavior:force-module-focus.
 * (The popup-era integration_oauth_complete storage bridge was removed with the
 * OAuth popup — connect is now a same-window redirect.)
 *
 * This hook is a CONTIGUOUS slice of what used to be inline, so hook count/order
 * is preserved exactly when ChatBox calls it at the same position the block
 * previously occupied. sendMessage / applyUiActions / appendUserMessage are
 * threaded in; refs and setters are threaded in BY REFERENCE so reads/writes
 * stay shared with ChatBox.
 */
import { useEffect } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { ChatMessage } from '@/features/chat/hooks/useChat';
import type { UseChatReturn } from '@/features/chat/hooks/useChat.types';
import type { Conversation } from '@/types/conversations';
import type { ChatUIAction } from '@/lib/api/endpoints/chat';

export interface UseChatWindowEventsDeps {
  activeConversation: Conversation | null;
  sendMessage: UseChatReturn['sendMessage'];
  appendUserMessage: UseChatReturn['appendUserMessage'];
  messages: ChatMessage[];
  applyUiActions: (actions: ChatUIAction[]) => void;
  userHasScrolledRef: MutableRefObject<boolean>;
  lastRouteModuleFocusKeyRef: MutableRefObject<string | null>;
  setArchivedInlineModuleIds: Dispatch<SetStateAction<Set<string>>>;
}

export function useChatWindowEvents(deps: UseChatWindowEventsDeps): void {
  const {
    activeConversation,
    sendMessage,
    appendUserMessage,
    messages,
    applyUiActions,
    userHasScrolledRef,
    lastRouteModuleFocusKeyRef,
    setArchivedInlineModuleIds,
  } = deps;

  // Listen for external send-chat-message events (e.g., from CRM Draft Email
  // button, or the Daily Summary quick-capture). `options` lets callers run the
  // turn silently (hideUserMessage/hideAssistantResponse) so the agent acts —
  // e.g. creating a calendar event/task — without cluttering the chat thread.
  useEffect(() => {
    const handleSendChatMessage = (
      event: CustomEvent<{
        message: string;
        options?: { hideUserMessage?: boolean; hideAssistantResponse?: boolean };
      }>,
    ) => {
      const { message, options } = event.detail;
      if (message && activeConversation?.id) {
        sendMessage(message, activeConversation.id, undefined, undefined, options);
      }
    };

    window.addEventListener('xavior:send-chat-message', handleSendChatMessage as EventListener);
    return () => {
      window.removeEventListener('xavior:send-chat-message', handleSendChatMessage as EventListener);
    };
  }, [activeConversation?.id, sendMessage]);

  // (The popup-era 'integration_oauth_complete' localStorage bridge was removed
  // with the OAuth popup. Connect is now a same-window redirect, so the page
  // reloads on return: the refreshed session + useOnboardingDiscovery drive the
  // post-connect onboarding state, and App.tsx's OAuth-return handler owns the
  // post-connect signals. Follow-up: a non-onboarding chat-initiated connect no
  // longer posts an "I just connected Gmail" nudge — reintroduce via a return
  // marker if the silent-after-connect chat case resurfaces.)

  // Listen for log-only chat messages (appear in UI but don't trigger agent)
  useEffect(() => {
    const handleLogChatMessage = (event: CustomEvent<{ message: string; moduleId?: string }>) => {
      const { message, moduleId } = event.detail;
      if (!message || !activeConversation?.id) return;

      // Don't add the same module back to back
      if (moduleId) {
        const lastMsg = messages[messages.length - 1];
        if (lastMsg?.inlineModuleId === moduleId) return;
      }

      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user' as const,
        content: message,
        timestamp: new Date(),
        inlineModuleId: moduleId,
      };
      // Force scroll to bottom so user sees the newly added module
      userHasScrolledRef.current = false;
      // Noise reduction: replace previous module navigation messages instead
      // of stacking them. Only the most recent "Show X" matters.
      // Chat may contain pairs: [user "Show X" + assistant ack], so strip both.
      appendUserMessage((prev) => {
        let trimmed = prev;
        while (trimmed.length > 0) {
          const last = trimmed[trimmed.length - 1];
          // Remove assistant ack for a view command
          if (last?.role === 'assistant' && last.routing?.startsWith('command:view')) {
            trimmed = trimmed.slice(0, -1);
            continue;
          }
          // Remove user "Show X" navigation message
          if (last?.inlineModuleId) {
            trimmed = trimmed.slice(0, -1);
            continue;
          }
          break;
        }
        return [...trimmed, userMessage];
      });
    };

    window.addEventListener('xavior:log-chat-message', handleLogChatMessage as EventListener);
    return () => {
      window.removeEventListener('xavior:log-chat-message', handleLogChatMessage as EventListener);
    };
  }, [activeConversation?.id, appendUserMessage, messages]);

  // Listen for module-archived events from sidebar to hide inline modules
  useEffect(() => {
    const handleModuleArchived = (event: CustomEvent<{ moduleId: string }>) => {
      const { moduleId } = event.detail;
      if (moduleId) {
        setArchivedInlineModuleIds((prev) => new Set(prev).add(moduleId));
      }
    };
    window.addEventListener('xavior:module-archived', handleModuleArchived as EventListener);
    return () => {
      window.removeEventListener('xavior:module-archived', handleModuleArchived as EventListener);
    };
  }, []);

  // Listen for force-module-focus events from sidebar (bypasses dedup)
  useEffect(() => {
    const handleForceModuleFocus = (event: CustomEvent<{ moduleId: string }>) => {
      const { moduleId } = event.detail;
      if (!moduleId || !activeConversation?.id) return;
      lastRouteModuleFocusKeyRef.current = null;
      applyUiActions([{
        type: 'module.focus',
        moduleId,
        conversationId: activeConversation.id,
      }]);
    };
    window.addEventListener('xavior:force-module-focus', handleForceModuleFocus as EventListener);
    return () => {
      window.removeEventListener('xavior:force-module-focus', handleForceModuleFocus as EventListener);
    };
  }, [activeConversation?.id, applyUiActions]);
}
